import { create } from "zustand";
import { devtools } from "zustand/middleware";
import {
  loginUser,
  registerUser,
  logoutUser,
  refreshUser,
  deleteUserAccount,
  verifyCode,
  processCV,
} from "../api/userApi";
import { AuthState, User } from "../types/api";
import { googleLogin } from "../api/socialLoginApi";
import { getProfileCvStatus } from "../api/profileApi";
import useProfileStore from "./useProfileStore";

const loadFromLocalStorage = <T>(key: string, defaultValue: T) => {
  const saved = localStorage.getItem(key);
  return saved ? JSON.parse(saved) : defaultValue;
};

const useAuthStore = create<AuthState>()(
  devtools(
    (set) => ({
      user: loadFromLocalStorage("user", null),
      access_token: loadFromLocalStorage("access_token", null),
      refresh_token: loadFromLocalStorage("refresh_token", null),
      isRefreshing: false,
      showAlert: loadFromLocalStorage("showAlert", null),
      cvId: loadFromLocalStorage("cvId", null),
      cvProcessing: loadFromLocalStorage("cvProcessing", null),

      register: async (user) => {
        try {
          const response = await registerUser(user);
          set({
            user: {
              full_name: user.first_name + " " + user.last_name,
              id: response.data.user_id,
              email: user.email,
              isLoggedIn: false,
            },
          });
          localStorage.setItem(
            "user",
            JSON.stringify({
              full_name: user.first_name + " " + user.last_name,
              id: response.data.user_id,
              email: user.email,
              isLoggedIn: false,
            })
          );
          return true;
        } catch (error) {
          throw error;
        }
      },

      verifyEmail: async (credentials) => {
        try {
          const response = await verifyCode(credentials);
          set({
            access_token: response.data.access,
            refresh_token: response.data.refresh,
          });
          localStorage.setItem(
            "access_token",
            JSON.stringify(response.data.access)
          );
          localStorage.setItem(
            "refresh_token",
            JSON.stringify(response.data.refresh)
          );
          return response;
        } catch (error) {
          throw error;
        }
      },

      onProcessingCv: async ({
        cvId,
        replace_personal_data = true,
        replace_profile_data = true,
      }) => {
        try {
          const response = await processCV({
            cvId,
            replace_personal_data,
            replace_profile_data,
          });
          set({
            cvId: response.data.id,
            cvProcessing: "processing",
          });
          localStorage.setItem("cvId", JSON.stringify(response.data.id));
          localStorage.setItem("cvProcessing", JSON.stringify("processing"));
          return response;
        } catch (error) {
          throw error;
        }
      },

      onCvStatus: async (cvId: number, access_token: string) => {
        try {
          const response = await getProfileCvStatus(cvId);
          set({
            cvProcessing: response.data.status,
          });

          localStorage.setItem(
            "cvProcessing",
            JSON.stringify(response.data.status)
          );
          return response;
        } catch (error) {
          throw error;
        }
      },

      login: async (credentials) => {
        try {
          const response = await loginUser(credentials);
          set({
            access_token: response.data.access,
            refresh_token: response.data.refresh,
            user: {
              id: response.data.user.id,
              full_name: response.data.user.full_name,
              email: response.data.user.email,
              password: null,
            },
          });

          localStorage.setItem("user", JSON.stringify(response.data.user));
          localStorage.setItem(
            "access_token",
            JSON.stringify(response.data.access)
          );
          localStorage.setItem(
            "refresh_token",
            JSON.stringify(response.data.refresh)
          );

          const rawChanges = localStorage.getItem("changes");

          let changes = null;

          if (rawChanges) {
            try {
              changes = JSON.parse(rawChanges);
            } catch (error) {
              console.error(
                "Помилка при парсингу localStorage 'changes':",
                error
              );
            }
          }
          if (changes) {
            set({
              showAlert: true,
            });
            localStorage.setItem("showAlert", JSON.stringify(true));
          }

          return true;
        } catch (error) {
          console.error("Login failed:", error);
          return false;
        }
      },

      logout: async (access_token, refresh_token) => {
        try {
          if (!access_token || !refresh_token) return false;
          await logoutUser({ access_token, refresh_token });

          return true;
        } catch (error) {
          console.error("Logout failed:", error);
          return false;
        } finally {
          localStorage.removeItem("user");
          localStorage.removeItem("access_token");
          localStorage.removeItem("refresh_token");
          localStorage.removeItem("questions");
          localStorage.removeItem("cvId");
          localStorage.removeItem("showAlert");
          localStorage.removeItem("reportProcessing");
          localStorage.removeItem("reportId");
          set({
            user: null,
            access_token: null,
            refresh_token: null,
          });
        }
      },
      deleteAccount: async (access_token, password) => {
        try {
          if (!access_token) return false;
          if (password) {
            await deleteUserAccount({ password });
          } else if (!password) {
            await deleteUserAccount({});
          }
          set({
            user: null,
            access_token: null,
            refresh_token: null,
          });
          const { setChanges } = useProfileStore.getState();
          setChanges(false);
          localStorage.removeItem("user");
          localStorage.removeItem("access_token");
          localStorage.removeItem("refresh_token");
          localStorage.removeItem("questions");
          localStorage.removeItem("cvId");
          localStorage.removeItem("success");
          localStorage.removeItem("cvProcessing");
          localStorage.removeItem("changes");
          localStorage.removeItem("userMarketChanges");
          localStorage.removeItem("showAlert");
          localStorage.removeItem("reportId");
          localStorage.removeItem("reportProcessing");

          return true;
        } catch (error) {
          console.error("Logout failed:", error);
          return false;
        }
      },

      refresh: async (access_token, refresh_token) => {
        set({
          isRefreshing: true,
        });
        if (!access_token || !refresh_token) {
          console.error("No tokens available for refresh.");
          return false;
        }

        try {
          const response = await refreshUser({ access_token, refresh_token });

          set({
            access_token: response.access,
            refresh_token: response.refresh,
            isRefreshing: false,
          });

          localStorage.setItem("access_token", JSON.stringify(response.access));
          localStorage.setItem(
            "refresh_token",
            JSON.stringify(response.refresh)
          );

          return response;
        } catch (error) {
          console.error("Token refresh failed:", error);
          set({
            user: null,
            access_token: null,
            refresh_token: null,
            isRefreshing: false,
          });
          localStorage.removeItem("user");
          localStorage.removeItem("access_token");
          localStorage.removeItem("refresh_token");
          localStorage.removeItem("questions");
          localStorage.removeItem("cvId");

          throw error;
        }
      },
      googleAuth: async (googleToken: string) => {
        try {
          const response = await googleLogin(googleToken);

          if (response) {
            if (!response.data.created) {
              set({
                access_token: response.data.access,
                refresh_token: response.data.refresh,
                user: {
                  id: response.data.user.id,
                  full_name:
                    response.data.user.first_name +
                    " " +
                    response.data.user.last_name,
                  email: response.data.user.email,
                  isLoggedIn: true,
                },
              });

              localStorage.setItem("user", JSON.stringify(response.data.user));
              localStorage.setItem(
                "access_token",
                JSON.stringify(response.data.access)
              );
              localStorage.setItem(
                "refresh_token",
                JSON.stringify(response.data.refresh)
              );

              const rawChanges = localStorage.getItem("changes");

              let changes = null;

              if (rawChanges) {
                try {
                  changes = JSON.parse(rawChanges);
                } catch (error) {
                  console.error(
                    "Помилка при парсингу localStorage 'changes':",
                    error
                  );
                }
              }
              if (changes) {
                set({
                  showAlert: true,
                });
                localStorage.setItem("showAlert", JSON.stringify(true));
              }

              return { status: "login" };
            } else if (response.data.created) {
              set({
                access_token: response.data.access,
                refresh_token: response.data.refresh,
                user: {
                  id: response.data.user.id,
                  full_name:
                    response.data.user.first_name +
                    " " +
                    response.data.user.last_name,
                  email: response.data.user.email,
                  isLoggedIn: false,
                },
              });

              localStorage.setItem("user", JSON.stringify(response.data.user));
              localStorage.setItem(
                "access_token",
                JSON.stringify(response.data.access)
              );
              localStorage.setItem(
                "refresh_token",
                JSON.stringify(response.data.refresh)
              );

              return { status: "register" };
            }
          }

          return false;
        } catch (error) {
          console.error("Login failed:", error);
          return false;
        }
      },
      setIsLoggedIn: (logged: boolean) => {
        const user = loadFromLocalStorage("user", null);
        set({
          user: {
            ...user,
            isLoggedIn: logged,
          },
        });
        localStorage.setItem(
          "user",
          JSON.stringify({ ...user, isLoggedIn: logged })
        );
      },
      setUser: (user: User) => {
        set({ user });
      },
      setShowAlert: (alert: boolean) => {
        localStorage.setItem("showAlert", JSON.stringify(alert));
        set({ showAlert: alert });
      },
    }),
    { name: "authStore" }
  )
);

export default useAuthStore;
